/// <amd-module name="Artifacts/FeedSettings/hooks/AddUpstreamForm" />
import {
  MAX_UPSTREAM_LENGTH,
  upstreamLocationsEqual,
} from "Artifacts/FeedSettings/helpers/UpstreamHelpers";
import { IFeedSettingsHubState } from "Artifacts/FeedSettings/redux";
import * as Res from "Artifacts/FeedSettings/Resources";
import { IUpstreamSource } from "Artifacts/FeedSettings/types";
import {
  getCurrentCollectionId,
  getCurrentCollectionName,
} from "Artifacts/Root/helpers/CollectionHelpers";
import { getCurrentSelectedProjectId } from "Artifacts/Root/helpers/ProjectHelpers";
import { getStore } from "Artifacts/Root/Redux";
import { isUpstreamSourceNameValid, isUrlValid } from "Artifacts/Root/Validator";
import { Feed, FeedView, UpstreamSourceType } from "Artifacts/Root/web-apis/contracts/Feed";
import { useEffect, useReducer, useState } from "react";
import * as StringUtils from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { useComponentContext } from "VSS/Platform/Layout";
import { Uri } from "VSS/Platform/Util/Url";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";

export interface AddUpstreamFormState {
  type: AddUpstreamType | null;
  upstreamSourceType: UpstreamSourceType | null;
  selectedPublicSource: IUpstreamSource | null;
  selectedSameOrgFeed: Feed | null;
  selectedView: FeedView | null;
  name: string;
  publicLocation: string;
  aadLocation: string;
  protocols: string[];
  isServiceConnectionInUse: boolean;
  serviceConnectionId: string;
}

export enum AddUpstreamType {
  Public,
  CollectionUpstream,
  AadUpstream,
}

export interface IAddUpstreamsSelections {
  feedViewDropdown: DropdownSelection;
  publicSourceDropdown: DropdownSelection;
  publicPackageTypeDropdown: DropdownSelection;
  sameOrgFeedDropdown: DropdownSelection;
}

export interface AddUpstreamFormItemError {
  description: string;
}
export interface IAddUpstreamsErrors {
  feedNameError?: AddUpstreamFormItemError;
  publicLocationError?: AddUpstreamFormItemError;
  feedSelectionError?: AddUpstreamFormItemError;
  viewSelectionError?: AddUpstreamFormItemError;
  aadLocationError?: AddUpstreamFormItemError;
  protocolSelectionError?: AddUpstreamFormItemError;
  serviceLocationError?: AddUpstreamFormItemError;
}

function validationError(
  error: keyof IAddUpstreamsErrors,
  description: string,
): (errors: IAddUpstreamsErrors) => IAddUpstreamsErrors {
  return (errors: IAddUpstreamsErrors): IAddUpstreamsErrors => ({
    ...errors,
    [error]: { description },
  });
}

function validationOk(
  error: keyof IAddUpstreamsErrors,
): (errors: IAddUpstreamsErrors) => IAddUpstreamsErrors {
  return (errors) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [error]: _, ...rest } = errors;
    return rest;
  };
}

// Errors with these descriptions aren't meant to be displayed to the user
// but they do prevent the form from being submitted
export enum HiddenFormValidationErrors {
  UserInputNotStartedError = "UserInputNotStarted",
}

export interface IUseAddUpstreamFormHook {
  formState: AddUpstreamFormState;
  dispatch: React.Dispatch<AddUpstreamFormAction>;
  selections: IAddUpstreamsSelections;
  errors: IAddUpstreamsErrors;
}

export interface AddUpstreamFormAction {
  type:
    | "type"
    | "name"
    | "publicLocation"
    | "aadLocation"
    | "selectedPublicSource"
    | "selectedOrgFeed"
    | "selectedView"
    | "protocol"
    | "appendProtocol"
    | "removeProtocol"
    | "isServiceConnectionInUse"
    | "serviceConnectionId"
    | "form";
  value:
    | boolean
    | string
    | AddUpstreamType
    | IUpstreamSource
    | Feed
    | FeedView
    | null
    | AddUpstreamFormState;
}

/**
 * useAddUpstreamForm hook manages add upstream form state, selections and validation.
 */
export function useAddUpstreamForm(): IUseAddUpstreamFormHook {
  const [formState, dispatch] = useReducer(addUpstreamFormReducer, getDefaultFormState());

  const [selections] = useState<IAddUpstreamsSelections>(() => ({
    feedViewDropdown: new DropdownSelection(),
    publicSourceDropdown: new DropdownSelection(),
    publicPackageTypeDropdown: new DropdownSelection(),
    sameOrgFeedDropdown: new DropdownSelection(),
    viewDropdown: new DropdownSelection(),
  }));
  const [errors] = useAddUpstreamFormValidation(formState, selections);

  return { formState, dispatch, selections, errors };
}

function addUpstreamFormReducer(
  state: AddUpstreamFormState,
  action: AddUpstreamFormAction,
): AddUpstreamFormState {
  switch (action.type) {
    case "type":
      return {
        ...state,
        type: action.value as AddUpstreamType | null,
        upstreamSourceType: addTypeToSourceType(action.value as AddUpstreamType | null),
      };
    case "publicLocation":
      return { ...state, publicLocation: action.value as string };
    case "aadLocation":
      return { ...state, aadLocation: action.value as string };
    case "selectedPublicSource":
      return { ...state, selectedPublicSource: action.value as IUpstreamSource | null };
    case "protocol":
      return { ...state, protocols: [action.value as string] };
    case "appendProtocol":
      return { ...state, protocols: [...state.protocols, action.value as string] };
    case "removeProtocol": {
      const index = state.protocols.indexOf(action.value as string);
      return {
        ...state,
        protocols: [...state.protocols.slice(0, index), ...state.protocols.slice(index + 1)],
      };
    }
    case "selectedOrgFeed":
      return { ...state, selectedSameOrgFeed: action.value as Feed | null, selectedView: null };
    case "selectedView":
      return { ...state, selectedView: action.value as FeedView | null };
    case "name":
      return { ...state, name: action.value as string };
    case "isServiceConnectionInUse":
      return { ...state, isServiceConnectionInUse: action.value as boolean };
    case "serviceConnectionId":
      return { ...state, serviceConnectionId: action.value as string };
    case "form":
      return { ...state, ...(action.value as AddUpstreamFormState) };
  }
}

function useAddUpstreamFormValidation(
  formState: AddUpstreamFormState,
  selections: IAddUpstreamsSelections,
): [IAddUpstreamsErrors] {
  const [errors, setErrors] = useState<IAddUpstreamsErrors>({});

  useUpstreamNameValidator(formState, setErrors);
  usePublicLocationValidator(formState, setErrors);
  useFeedDropdownValidator(formState, setErrors, selections);
  useViewDropdownValidator(formState, setErrors, selections);
  useAadLocationValidator(formState, setErrors);
  useProtocolValidator(formState, setErrors);
  useServiceLocationValidator(formState, setErrors);

  return [errors];
}

function useUpstreamNameValidator(
  formState: AddUpstreamFormState,
  setErrors: React.Dispatch<React.SetStateAction<IAddUpstreamsErrors>>,
): void {
  const { pageContext } = useComponentContext();
  const [userHasStartedTyping, setUserHasStartedTyping] = useState<boolean>(false);

  useEffect(() => {
    // skip validator check until user starts typing
    if (!userHasStartedTyping) {
      if (formState.name) {
        setUserHasStartedTyping(true);
      } else {
        return setErrors(
          validationError("feedNameError", HiddenFormValidationErrors.UserInputNotStartedError),
        );
      }
    }
    if (formState.name.length > MAX_UPSTREAM_LENGTH) {
      return setErrors(
        validationError(
          "feedNameError",
          StringUtils.format(
            Res.AddUpstreamPanel_ValidationError_NameLength,
            MAX_UPSTREAM_LENGTH.toString(),
          ),
        ),
      );
    }
    if (formState.name.length === 0) {
      return setErrors(
        validationError("feedNameError", Res.AddUpstreamPanel_ValidationError_NameEmpty),
      );
    }
    const feedSettingsState = getStore(pageContext).getState()["feed-settings"]
      .hubState as IFeedSettingsHubState;
    const existingSourceWithSameName = feedSettingsState.settings?.upstreamSources.find((source) =>
      StringUtils.equals(source.name, formState.name, true),
    );
    // Check if valid upstream name
    if (!isUpstreamSourceNameValid(formState.name)) {
      return setErrors(
        validationError(
          "feedNameError",
          Res.AddUpstreamPanel_ValidationError_BadName,
        ),
      );
    }
    if (
      // Source exists with the same name
      existingSourceWithSameName &&
      // and is not targeting the same feed view
      !(
        (formState.selectedSameOrgFeed &&
          formState.selectedView &&
          formState.selectedSameOrgFeed.id === existingSourceWithSameName.internalUpstreamFeedId &&
          formState.selectedView.id === existingSourceWithSameName.internalUpstreamViewId) ||
        (formState.aadLocation && formState.aadLocation === existingSourceWithSameName.location)
      )
    ) {
      return setErrors(
        validationError("feedNameError", Res.AddUpstreamPanel_ValidationError_NameInUse),
      );
    }

    return setErrors(validationOk("feedNameError"));
  }, [
    formState.name,
    setErrors,
    pageContext,
    userHasStartedTyping,
    formState.selectedSameOrgFeed,
    formState.selectedView,
    formState.aadLocation,
  ]);
}

function usePublicLocationValidator(
  formState: AddUpstreamFormState,
  setErrors: React.Dispatch<React.SetStateAction<IAddUpstreamsErrors>>,
): void {
  const { pageContext } = useComponentContext();

  useEffect(() => {
    if (formState.type === AddUpstreamType.Public) {
      if (formState.publicLocation === "") {
        // Check if empty
        return setErrors(
          validationError(
            "publicLocationError",
            Res.AddUpstreamPanel_ValidationError_PublicLocationEmpty,
          ),
        );
      }

      // Check if valid URL
      if (!isUrlValid(formState.publicLocation)) {
        return setErrors(
          validationError(
            "publicLocationError",
            Res.AddUpstreamPanel_ValidationError_InvalidLocation,
          ),
        );
      }

      // Check if location is already in use.
      const feedSettingsState = getStore(pageContext).getState()["feed-settings"]
        .hubState as IFeedSettingsHubState;
      if (
        feedSettingsState.settings?.upstreamSources
          .map((source) => source.location)
          .some((location) => upstreamLocationsEqual(location || "", formState.publicLocation))
      ) {
        return setErrors(
          validationError(
            "publicLocationError",
            Res.AddUpstreamPanel_ValidationError_LocationInUse,
          ),
        );
      }
    }

    return setErrors(validationOk("publicLocationError"));
  }, [formState.publicLocation, formState.type, setErrors, pageContext]);
}

function useFeedDropdownValidator(
  formState: AddUpstreamFormState,
  setErrors: React.Dispatch<React.SetStateAction<IAddUpstreamsErrors>>,
  selections: IAddUpstreamsSelections,
) {
  useEffect(() => {
    if (formState.type === AddUpstreamType.CollectionUpstream) {
      if (selections.sameOrgFeedDropdown.selectedCount === 0) {
        return setErrors(
          validationError(
            "feedSelectionError",
            Res.AddUpstreamPanel_ValidationError_FeedMustBeSelected,
          ),
        );
      }
    }

    return setErrors(validationOk("feedSelectionError"));
  }, [setErrors, selections.sameOrgFeedDropdown, formState.type, formState.selectedSameOrgFeed]);
}

function useViewDropdownValidator(
  formState: AddUpstreamFormState,
  setErrors: React.Dispatch<React.SetStateAction<IAddUpstreamsErrors>>,
  selections: IAddUpstreamsSelections,
) {
  useEffect(() => {
    if (formState.type === AddUpstreamType.CollectionUpstream) {
      if (selections.feedViewDropdown.selectedCount === 0) {
        return setErrors(
          validationError(
            "viewSelectionError",
            Res.AddUpstreamPanel_ValidationError_ViewMustBeSelected,
          ),
        );
      }
    }

    return setErrors(validationOk("viewSelectionError"));
  }, [setErrors, selections, formState.selectedView, formState.type]);
}

function useProtocolValidator(
  formState: AddUpstreamFormState,
  setErrors: React.Dispatch<React.SetStateAction<IAddUpstreamsErrors>>,
) {
  useEffect(() => {
    if (
      formState.type === AddUpstreamType.CollectionUpstream ||
      formState.type === AddUpstreamType.AadUpstream
    ) {
      if (formState.protocols.length === 0) {
        return setErrors(
          validationError(
            "protocolSelectionError",
            Res.AddUpstreamPanel_ValidationError_ProtocolMustBeSelected,
          ),
        );
      }
    }

    return setErrors(validationOk("protocolSelectionError"));
  }, [setErrors, formState.protocols, formState.type]);
}

function useAadLocationValidator(
  formState: AddUpstreamFormState,
  setErrors: React.Dispatch<React.SetStateAction<IAddUpstreamsErrors>>,
) {
  const [userHasStartedTyping, setUserHasStartedTyping] = useState<boolean>(false);

  // Validate AAD feed location.
  useEffect(() => {
    if (formState.type === AddUpstreamType.AadUpstream) {
      // skip validator check until user starts typing
      if (!userHasStartedTyping) {
        if (formState.aadLocation) {
          setUserHasStartedTyping(true);
        } else {
          return setErrors(
            validationError(
              "aadLocationError",
              HiddenFormValidationErrors.UserInputNotStartedError,
            ),
          );
        }
      }
      if (formState.aadLocation.length === 0) {
        return setErrors(
          validationError("aadLocationError", Res.AddUpstreamPanel_ValidationError_LocationEmpty),
        );
      }
      const uri = new Uri(formState.aadLocation);
      // regex with capture either
      // project/feed@view/error  -- match[1] == "project", match[2] == "feed", match[3] == "view", match[4] == "error"
      // feed@view/error          -- match[1] == "feed", match[2] == null, match[3] == "view", match[4] == "error"
      const matches = uri.path.match(/\/*([^\/]*)\/?([^\/]*)@([^\/]+)\/*(.*)/i);
      if (
        !uri ||
        uri.scheme.length === 0 ||
        uri.path.length === 0 ||
        uri.path.indexOf("@") < 0 ||
        !matches ||
        matches[4].length > 0
      ) {
        return setErrors(
          validationError(
            "aadLocationError",
            StringUtils.format(
              Res.AddUpstreamPanel_ValidationError_Location_FormatCouldNotBeParsed,
              Res.AddUpstreamPanel_ValidationError_Location_Example,
            ),
          ),
        );
      }
    }
    return setErrors(validationOk("aadLocationError"));
  }, [formState.aadLocation, setErrors, formState.type, userHasStartedTyping]);
}

function useServiceLocationValidator(
  formState: AddUpstreamFormState,
  setErrors: React.Dispatch<React.SetStateAction<IAddUpstreamsErrors>>,
) {
  // Validate AAD feed location.
  useEffect(() => {
    if (formState.type === AddUpstreamType.AadUpstream && formState.isServiceConnectionInUse) {
      if (formState.serviceConnectionId === "") {
        return setErrors(
          validationError(
            "serviceLocationError",
            Res.AddUpstreamPanel_ValidationError_ServiceLocationMustBeSelected,
          ),
        );
      }
    }
    return setErrors(validationOk("serviceLocationError"));
  }, [
    formState.serviceConnectionId,
    formState.isServiceConnectionInUse,
    setErrors,
    formState.type,
  ]);
}

function addTypeToSourceType(type: AddUpstreamType | null): UpstreamSourceType | null {
  if (type == null) {
    return null;
  }

  switch (type) {
    case AddUpstreamType.Public:
      return UpstreamSourceType.Public;
    case AddUpstreamType.CollectionUpstream:
    case AddUpstreamType.AadUpstream:
      return UpstreamSourceType.Internal;
  }
}

export function formDataToUpstreamSources(
  pageContext: IVssPageContext,
  formState: AddUpstreamFormState,
): IUpstreamSource[] {
  if (formState.upstreamSourceType == null) {
    throw new Error("upstreamSourceType cannot be empty.");
  }

  if (formState.selectedPublicSource != null) {
    return [formState.selectedPublicSource];
  }

  const location = getLocation(formState, pageContext);

  const serviceEndpointData = prepareServiceEndpointData(pageContext, formState);

  return formState.protocols.map((protocol) => ({
    id: null,
    displayLocation: null,
    internalUpstreamCollectionId:
      formState.selectedSameOrgFeed != null ? getCurrentCollectionId(pageContext) : null,
    internalUpstreamFeedId: formState.selectedSameOrgFeed?.id || null,
    internalUpstreamProjectId: formState.selectedSameOrgFeed?.project?.id || null,
    internalUpstreamViewId: formState.selectedView?.id || null,
    location,
    name: formState.name,
    protocol,
    upstreamSourceType: formState.upstreamSourceType as UpstreamSourceType,
    ...serviceEndpointData,
  }));
}

function getLocation(formState: AddUpstreamFormState, pageContext: IVssPageContext): string | null {
  if (formState.selectedSameOrgFeed != null) {
    return buildFakeLocation(pageContext, formState);
  } else if (formState.publicLocation !== "") {
    return formState.publicLocation;
  } else if (formState.aadLocation !== "") {
    return formState.aadLocation;
  } else {
    return null;
  }
}

function getDefaultFormState(): AddUpstreamFormState {
  return {
    type: null,
    name: "",
    publicLocation: "",
    aadLocation: "",
    protocols: [],
    selectedPublicSource: null,
    upstreamSourceType: null,
    selectedSameOrgFeed: null,
    selectedView: null,
    isServiceConnectionInUse: false,
    serviceConnectionId: "",
  };
}

function buildFakeLocation(pageContext: IVssPageContext, formState: AddUpstreamFormState): string {
  if (formState.selectedSameOrgFeed == null) {
    throw new Error("Fake location can be created only for same org feeds.");
  }
  if (formState.selectedView == null) {
    throw new Error("Cannot create fake location without a feed.");
  }

  const feed = formState.selectedSameOrgFeed;
  const projectSegment = (feed.project && `${feed.project.name}/`) || "";
  return `azure-feed://${getCurrentCollectionName(pageContext)}/${projectSegment}${feed.name}@${
    formState.selectedView.name
  }`;
}

function prepareServiceEndpointData(pageContext: IVssPageContext, formState: AddUpstreamFormState) {
  if (formState.type === AddUpstreamType.AadUpstream) {
    return {
      serviceEndpointId:
        formState.serviceConnectionId === "" ? null : formState.serviceConnectionId,
      serviceEndpointProjectId: getCurrentSelectedProjectId(pageContext),
    };
  } else {
    return {};
  }
}

export function resetFormAndSelectionState(
  dispatch: React.Dispatch<AddUpstreamFormAction>,
  selections: IAddUpstreamsSelections,
): void {
  dispatch({ type: "form", value: getDefaultFormState() });

  selections.feedViewDropdown.clear();
  selections.publicSourceDropdown.clear();
  selections.publicPackageTypeDropdown.clear();
  selections.sameOrgFeedDropdown.clear();
}
